'use client';

import { fetchProjects, projectsQueryKeys } from '@/lib/api/projects';
import { styled } from '@linaria/react';
import { useQuery } from '@tanstack/react-query';
import { ProjectCard } from './ProjectCard';
import { DitherBackground } from './dither-background/DitherBackground';
import "./ProjectsSection.linaria.module.css!=!./ProjectsSection?./ProjectsSection.linaria.module.css?css=H4sIAAAAAAAAA61U7Y6jOBD8P0/h29FJcBuQTQKTsaXTvYoDDfEGbM42+UK8+wkHAplkNru6ifLD6e5UV7nLHdZkz80PLttaGWGFklRDya3YAzsHQmZwpJjVPMuELGglpLfcHxYo0lD5YzjYKGtV5bJvfTa5yVpV05SXqbfWUKHv6BbEZxXXhZCuLOhL2Ianu0KrRma0FBK4DgrNMwHSegnOoFigV5LhZRQv0Cte4wSvF+h1BVGMsc+6cFSE/v6rHSWQWZxuIFcaJsF8Y1TZ2EkwYamSFqSl39A3dss/QFF99FkJuaWYHURmt5Rg/Cfbgii2lkb4iQSr0OW+FkgXGw8jjLDvztzDCzR8w9V9zJ+rozy3oH8iAt+IGEaEPyeePOdtVf2RFPl16i9hTY51uavWbXCAzU7YIFVlU8mg4DXtzcA+/u7+qSATHHmVkMGFMlr1rmk/wzJueOOpewSQJE8QIjadHiK8r58gLNl06rprXe/Iwe3DPC4qX8KUxIemqEhb8ePQhRDnpEs9xYg3VrH7R9q9hIaIf5t0dWwtHG3AS1FImoK0oNltt9XQzRzhfFq/t7mSNjDiDO7pxhqqBVruD+g7WroX7PKHiz3ecG+oUmn6mrvPB2wykXVPGY+9yA84J/o0a0bCcdg9GsccUswm4fED3b0Zg8GoJEyuCwm7bUIc82h/cC4rZaSbHXlwG7cd54Qc+xF0HAqQhJxPxfvnSHmcxEn8HCk74xrL5uoTq7kcBqlqngp7QmuDgBsIhAxUY9nvVAQZlPxEcRgbNhT3O/tuM7ixfL4A3HnaHmoPOi/Vge6FEZsSWDfKoFtuvJTLPTd+OzYkTud2me4S0bpOf4iqVtpyaYees8DUfRac8ZhF73TMcuN95qK0oGm/8DQ31lv5aKN7MAnGeDiMfXTNrcOP2VXsI8Nto7kFD4dvK599OeB1jdQactAmGP9OUQnG+O317r5G09tXa3oA2P1EVKU0+EjpKacha1LILt6vuQaZnii6RP+n/OiOLbll+/576n8Fr+te/gP6t0hjPAkAAA=="
export const PROJECT_COLUMN_MAX_WIDTH_REMS = 30;
export const PROJECT_COLUMNS_MAX = 3;
export const PROJECTS_SECTION_GAP_REMS = 2;
const ProjectsSectionContainer = /*#__PURE__*/styled('section')({
  name: "ProjectsSectionContainer",
  class: "p1vasjan",
  propsAsIs: false
});
export function getMediaQueryMinWidthForColumnCount(columns: number) {
  if (columns < 1) throw new TypeError("Columns must be at least 1");
  if (columns > PROJECT_COLUMNS_MAX) throw new TypeError(`Columns cannot be more than ${PROJECT_COLUMNS_MAX}`);
  return `(min-width: ${(columns === 1 ? 0 : columns) * PROJECT_COLUMN_MAX_WIDTH_REMS + (columns + 1) * PROJECTS_SECTION_GAP_REMS}rem)`;
}
const ProjectsMasonry = /*#__PURE__*/styled('div')({
  name: "ProjectsMasonry",
  class: "p1xplkm8",
  propsAsIs: false
});
const Container = /*#__PURE__*/styled('div')({
  name: "Container",
  class: "c15wugm1",
  propsAsIs: false
});
const SectionHeader = /*#__PURE__*/styled('div')({
  name: "SectionHeader",
  class: "s1iquc4x",
  propsAsIs: false
});
const SectionTitle = /*#__PURE__*/styled('h2')({
  name: "SectionTitle",
  class: "sxezy89",
  propsAsIs: false
});
const SectionDescription = /*#__PURE__*/styled('p')({
  name: "SectionDescription",
  class: "s1jez6ry",
  propsAsIs: false
});
const LoadingState = /*#__PURE__*/styled('div')({
  name: "LoadingState",
  class: "ln2ruk1",
  propsAsIs: false
});
const ErrorState = /*#__PURE__*/styled('div')({
  name: "ErrorState",
  class: "e161zyg9",
  propsAsIs: false
});
export const DitherBackgroundWrapper = /*#__PURE__*/styled('div')({
  name: "DitherBackgroundWrapper",
  class: "dz0p0nu",
  propsAsIs: false
});
export const ditherBackgroundContainerClass = "dh3ck6i";
export function ProjectsSection() {
  const {
    data: projectsResponse,
    isLoading,
    error
  } = useQuery({
    queryKey: projectsQueryKeys.all,
    queryFn: fetchProjects,
    staleTime: 60 * 60 * 1000 // 1 hour
  });
  const header = <SectionHeader>
		<SectionTitle>Projects. Projects everywhere.</SectionTitle>
		<SectionDescription>
			No programmer is complete without a wealth of hobby projects to show off!
		</SectionDescription>
	</SectionHeader>;
  if (isLoading) {
    return <ProjectsSectionContainer id="projects">
			<DitherBackground />
			<Container>
				{header}
				<LoadingState>Loading projects...</LoadingState>
			</Container>
		</ProjectsSectionContainer>;
  }
  if (error) {
    return <ProjectsSectionContainer id="projects">
			<DitherBackground />
			<Container>
				{header}
				<ErrorState>Failed to load projects. Please try again later.</ErrorState>
			</Container>
		</ProjectsSectionContainer>;
  }
  const projects = projectsResponse?.data || [];
  return <ProjectsSectionContainer id="projects">
		<DitherBackgroundWrapper>
			<DitherBackground />
		</DitherBackgroundWrapper>
		<Container>
			{header}
			<ProjectsMasonry>
				{projects.map(project => <ProjectCard key={project.id} project={project} />)}
			</ProjectsMasonry>
		</Container>
	</ProjectsSectionContainer>;
}